import { Direction } from "../../../types/Direction";
import _ from "lodash";
import {
    Count,
    CountType,
    cumulativeSum,
    formatIntervalBound,
    CountValue,
} from "../../../types/Count";
import {
    toTemporalZonedDateTime,
    formatDate as temporalFormatDate,
    getTimezoneAbbr,
    startOf,
    endOf,
    toDate,
} from "../../../shared/lib/temporal";
import { DeviceCounts } from "./Report";
import { TimeUnit } from "../../../shared/lib/temporal/types";

function normalizeUnit(
    unit: TimeUnit,
): "day" | "hour" | "minute" | "month" | "year" {
    return unit === "date" ? "day" : unit;
}

function hourBucketStart(zdt: Temporal.ZonedDateTime): Temporal.ZonedDateTime {
    const start = startOf(zdt, "hour");
    // Bucket to next hour when minute >= 30 (e.g., 08:30 -> 09:00)
    return zdt.minute >= 30 ? start.add({ hours: 1 }) : start;
}

function bucketKeyZdt(
    zdt: Temporal.ZonedDateTime,
    unit: "day" | "hour" | "minute" | "month" | "year",
): Temporal.ZonedDateTime {
    switch (unit) {
        case "hour":
            return hourBucketStart(zdt);
        case "day":
        case "minute":
        case "month":
        case "year":
            return endOf(zdt, unit);
    }
}

function formatDate(date: Date, timezone: string, unit: TimeUnit): string {
    const zdt = toTemporalZonedDateTime(date, timezone);
    const dateTemplate = "YYYY-MM-DD";
    const timeTemplate = [];

    let adjusted = zdt;
    if (unit === "minute" || unit === "hour") {
        timeTemplate.push("HH:mm");
        if (unit === "hour") {
            adjusted = startOf(zdt, "hour");
        }
    }
    const base = temporalFormatDate(
        adjusted,
        [dateTemplate, ...timeTemplate].join(" "),
    ).trimEnd();

    // Append timezone abbreviation for hour-level exports to disambiguate DST hours
    if (unit === "hour") {
        const tzAbbr = getTimezoneAbbr(adjusted);
        return `${base} (${tzAbbr})`;
    }
    return base;
}

enum Period {
    Instant = "instant",
    Cumulative = "cumulative",
}

interface CountHeader {
    direction: Direction | "Counts";
    realDirection?: Direction; // For unidirectional devices, the actual direction
    period: Period | null;
    type: CountType;
}

function formatHeader(countHeader: CountHeader): string {
    return `${countHeader.direction} ${countHeader.period === Period.Cumulative ? countHeader.period.charAt(0).toUpperCase() + countHeader.period.substring(1) + " " : ""}${formatIntervalBound(countHeader.type)}`;
}

export const formatCSVData = (
    data: DeviceCounts,
    reportedCountType: CountType.Adjusted | CountType.Predicted,
    timezone: string,
    unit: TimeUnit,
): string => {
    const headers: string[] = [
        String(unit).charAt(0).toUpperCase() + String(unit).slice(1),
    ];

    const uniqueHeaders = new Set<string>();
    // Collect deviceHeaders per device for later use
    const deviceHeadersPerDevice: CountHeader[][] = Object.values(data).map(
        (deviceCounts) => {
            const isUnidirectional =
                deviceCounts.device.configs?.[0]?.directionality ===
                "UNIDIRECTIONAL";
            const direction =
                deviceCounts.device.configs?.[0]?.directions?.dir1 ===
                "UPSTREAM"
                    ? Direction.Upstream
                    : Direction.Downstream;
            const videoOnly =
                deviceCounts.device.configs?.[0]?.mode === "VIDEO_ONLY";
            if (videoOnly) {
                return [];
            }
            const baseHeaders: Omit<CountHeader, "type">[] = isUnidirectional
                ? [
                      {
                          direction: "Counts",
                          realDirection: direction, // store the real direction
                          period: Period.Instant,
                      },
                      {
                          direction: "Counts",
                          realDirection: direction, // store the real direction
                          period: Period.Cumulative,
                      },
                  ]
                : [
                      {
                          direction: Direction.Upstream,
                          period: Period.Instant,
                      },
                      {
                          direction: Direction.Downstream,
                          period: Period.Instant,
                      },
                      {
                          direction: Direction.Net,
                          period: Period.Instant,
                      },
                      {
                          direction: Direction.Net,
                          period: Period.Cumulative,
                      },
                  ];

            const headerSuffixes: CountType[] =
                reportedCountType === CountType.Adjusted
                    ? [
                          CountType.LowerBound,
                          reportedCountType,
                          CountType.UpperBound,
                      ]
                    : [reportedCountType];

            return baseHeaders.flatMap((base) =>
                headerSuffixes.map((suffix) => ({
                    ...base,
                    type: suffix,
                })),
            );
        },
    );

    // Flatten all headers for all devices for CSV header row
    deviceHeadersPerDevice.forEach((deviceHeaders, idx) => {
        const deviceCounts = Object.values(data)[idx];
        deviceHeaders.forEach((header) => {
            const formattedHeader = `${deviceCounts.device.displayName} ${formatHeader(header)}`;
            if (!uniqueHeaders.has(formattedHeader)) {
                headers.push(formattedHeader);
                uniqueHeaders.add(formattedHeader);
            }
        });
    });

    if (headers.length <= 1) return "";

    if (Object.keys(data).length === 0) {
        return headers.join(",");
    }

    const deviceCountsArr = Object.values(data);

    // create a map of counts keyed by the date.
    const cumulativeMap = deviceCountsArr.map((deviceCounts) => {
        return _.reduce(
            deviceCounts.counts,
            (acc, item) => {
                if (!item?.timestamp) {
                    return acc;
                }
                const zdt = toTemporalZonedDateTime(item.timestamp, timezone);
                const unitNorm = normalizeUnit(unit);
                const bucketZdt = bucketKeyZdt(zdt, unitNorm);
                const sensorTime = toDate(bucketZdt).getTime();
                acc[sensorTime] = item;
                return acc;
            },
            {} as Record<number, Count>,
        );
    });

    // reduce each day for each device down to a singular count and cumulative.
    const deviceDailyCounts = _(deviceCountsArr)
        .map((device, index) => {
            const countsByDay = _.groupBy(
                device.counts.filter((c) => !!c?.timestamp),
                (instant) => {
                    const zdt = toTemporalZonedDateTime(
                        instant.timestamp,
                        timezone,
                    );
                    const unitNorm = normalizeUnit(unit);
                    return toDate(bucketKeyZdt(zdt, unitNorm)).toISOString();
                },
            );
            return _.map(countsByDay, (instants, time) => {
                const observedInstants = instants.filter(
                    (count) => !count.imputed,
                );
                const reducedGroupedInstant = cumulativeSum(instants).at(-1)!;
                const lastGroupCumulative =
                    cumulativeMap[index][new Date(time).getTime()];
                return {
                    timestamp: (() => {
                        const endMs = new Date(time).getTime();
                        const endZdt = toTemporalZonedDateTime(endMs, timezone);
                        return toDate(endZdt).toISOString();
                    })(),
                    count: {
                        instant: {
                            [Direction.Upstream]: {
                                [CountType.Predicted]:
                                    reducedGroupedInstant!.cumulative!.Upstream
                                        .Predicted!,
                                [CountType.LowerBound]:
                                    reducedGroupedInstant!.cumulative!.Upstream
                                        .LowerBound!,
                                [CountType.UpperBound]:
                                    reducedGroupedInstant!.cumulative!.Upstream
                                        .UpperBound!,
                                [CountType.Adjusted]:
                                    reducedGroupedInstant!.cumulative!.Upstream
                                        .Adjusted!,
                            },
                            [Direction.Downstream]: {
                                [CountType.Predicted]:
                                    reducedGroupedInstant!.cumulative!
                                        .Downstream.Predicted!,
                                [CountType.LowerBound]:
                                    reducedGroupedInstant!.cumulative!
                                        .Downstream.LowerBound!,
                                [CountType.UpperBound]:
                                    reducedGroupedInstant!.cumulative!
                                        .Downstream.UpperBound!,
                                [CountType.Adjusted]:
                                    reducedGroupedInstant!.cumulative!
                                        .Downstream.Adjusted!,
                            },
                            [Direction.Net]: {
                                [CountType.Predicted]:
                                    reducedGroupedInstant!.cumulative!.Net
                                        .Predicted!,
                                [CountType.LowerBound]:
                                    reducedGroupedInstant!.cumulative!.Net
                                        .LowerBound!,
                                [CountType.UpperBound]:
                                    reducedGroupedInstant!.cumulative!.Net
                                        .UpperBound!,
                                [CountType.Adjusted]:
                                    reducedGroupedInstant!.cumulative!.Net
                                        .Adjusted!,
                            },
                        },
                        cumulative: {
                            [Direction.Upstream]: {
                                [CountType.Predicted]:
                                    lastGroupCumulative.cumulative!.Upstream
                                        .Predicted!,
                                [CountType.LowerBound]:
                                    lastGroupCumulative.cumulative!.Upstream
                                        .LowerBound!,
                                [CountType.UpperBound]:
                                    reducedGroupedInstant!.cumulative!.Upstream
                                        .UpperBound!,
                                [CountType.Adjusted]:
                                    reducedGroupedInstant!.cumulative!.Upstream
                                        .Adjusted!,
                            },
                            [Direction.Downstream]: {
                                [CountType.Predicted]:
                                    lastGroupCumulative.cumulative!.Downstream
                                        .Predicted!,
                                [CountType.LowerBound]:
                                    lastGroupCumulative.cumulative!.Downstream
                                        .LowerBound!,
                                [CountType.UpperBound]:
                                    lastGroupCumulative.cumulative!.Downstream
                                        .UpperBound!,
                                [CountType.Adjusted]:
                                    lastGroupCumulative.cumulative!.Downstream
                                        .Adjusted!,
                            },
                            [Direction.Net]: {
                                [CountType.Predicted]:
                                    lastGroupCumulative.cumulative!.Net
                                        .Predicted!,
                                [CountType.LowerBound]:
                                    lastGroupCumulative.cumulative!.Net
                                        .LowerBound!,
                                [CountType.UpperBound]:
                                    lastGroupCumulative.cumulative!.Net
                                        .UpperBound!,
                                [CountType.Adjusted]:
                                    lastGroupCumulative.cumulative!.Net
                                        .Adjusted!,
                            },
                        },
                    },
                    totalOutage: observedInstants.length === 0,
                };
            });
        })
        .value();

    // zip the daily counts so that the multi-dimensional array has
    // the shape [ [day-1 device 1 counts, day-2 device 2 counts, ...], ... ]
    const dailyCounts = _.reduce(
        _.zip(...deviceDailyCounts),
        (acc, dailyDevicesCounts) => {
            if (dailyDevicesCounts && dailyDevicesCounts[0]) {
                const zdt = toTemporalZonedDateTime(
                    dailyDevicesCounts[0].timestamp,
                    timezone,
                );
                const unitNorm = normalizeUnit(unit);
                const time = toDate(bucketKeyZdt(zdt, unitNorm)).getTime();

                if (!acc[time]) {
                    acc[time] = [];
                }

                dailyDevicesCounts.forEach((count, index) => {
                    if (!acc[time][index]) {
                        acc[time][index] = {
                            instant: {
                                [Direction.Upstream]: {
                                    [CountType.Predicted]:
                                        count?.count.instant.Upstream.Predicted,
                                    [CountType.LowerBound]:
                                        count?.count.instant.Upstream
                                            .LowerBound,
                                    [CountType.UpperBound]:
                                        count?.count.instant.Upstream
                                            .UpperBound,
                                    [CountType.Adjusted]:
                                        count?.count.instant.Upstream.Adjusted,
                                },
                                [Direction.Downstream]: {
                                    [CountType.Predicted]:
                                        count?.count.instant.Downstream
                                            .Predicted,
                                    [CountType.LowerBound]:
                                        count?.count.instant.Downstream
                                            .LowerBound,
                                    [CountType.UpperBound]:
                                        count?.count.instant.Downstream
                                            .UpperBound,
                                    [CountType.Adjusted]:
                                        count?.count.instant.Downstream
                                            .Adjusted,
                                },
                                [Direction.Net]: {
                                    [CountType.Predicted]:
                                        count?.count.instant.Net.Predicted,
                                    [CountType.LowerBound]:
                                        count?.count.instant.Net.LowerBound,
                                    [CountType.UpperBound]:
                                        count?.count.instant.Net.UpperBound,
                                    [CountType.Adjusted]:
                                        count?.count.instant.Net.Adjusted,
                                },
                            },
                            cumulative: {
                                [Direction.Upstream]: {
                                    [CountType.Predicted]:
                                        count?.count.cumulative.Upstream
                                            .Predicted,
                                    [CountType.LowerBound]:
                                        count?.count.cumulative.Upstream
                                            .LowerBound,
                                    [CountType.UpperBound]:
                                        count?.count.cumulative.Upstream
                                            .UpperBound,
                                    [CountType.Adjusted]:
                                        count?.count.cumulative.Upstream
                                            .Adjusted,
                                },
                                [Direction.Downstream]: {
                                    [CountType.Predicted]:
                                        count?.count.cumulative.Downstream
                                            .Predicted,
                                    [CountType.LowerBound]:
                                        count?.count.cumulative.Downstream
                                            .LowerBound,
                                    [CountType.UpperBound]:
                                        count?.count.cumulative.Downstream
                                            .UpperBound,
                                    [CountType.Adjusted]:
                                        count?.count.cumulative.Downstream
                                            .Adjusted,
                                },
                                [Direction.Net]: {
                                    [CountType.Predicted]:
                                        count?.count.cumulative.Net.Predicted,
                                    [CountType.LowerBound]:
                                        count?.count.cumulative.Net.LowerBound,
                                    [CountType.UpperBound]:
                                        count?.count.cumulative.Net.UpperBound,
                                    [CountType.Adjusted]:
                                        count?.count.cumulative.Net.Adjusted,
                                },
                            },
                            totalOutage: count!.totalOutage,
                        };
                    }
                });
            }
            return acc;
        },
        {} as Record<
            number,
            ({
                totalOutage: boolean;
            } & CountValue)[]
        >,
    );

    // iterate through the dailyCounts, creating a row for each
    // entry in dailyCounts. Any records with a total outage
    // will have their values replaced with empty strings.
    const dataRows = Object.entries(dailyCounts).map(([day, counts]) => {
        const row: string[] = [
            formatDate(new Date(Number(day)), timezone, unit),
        ];

        counts.forEach((count, idx) => {
            const deviceHeaders = deviceHeadersPerDevice[idx];

            if (!count || count.totalOutage) {
                row.push(...new Array(deviceHeaders.length).fill(""));
            } else {
                deviceHeaders.forEach((header) => {
                    if (header.direction === "Counts") {
                        // For unidirectional devices, use the real direction to pull the value
                        row.push(
                            header.period
                                ? count[header.period]?.[
                                      header.realDirection!
                                  ]?.[header.type] != null
                                    ? String(
                                          count[header.period]?.[
                                              header.realDirection!
                                          ]?.[header.type],
                                      )
                                    : ""
                                : "",
                        );
                    } else if (
                        header.direction === Direction.Upstream ||
                        header.direction === Direction.Downstream ||
                        header.direction === Direction.Net
                    ) {
                        row.push(
                            header.period
                                ? count[header.period]?.[header.direction]?.[
                                      header.type
                                  ] != null
                                    ? String(
                                          count[header.period]?.[
                                              header.direction
                                          ]?.[header.type],
                                      )
                                    : ""
                                : "",
                        );
                    } else {
                        // For Mode.VideoOnly, always push empty string
                        row.push("-");
                    }
                });
            }
        });
        return row;
    });
    return [headers, ...dataRows].map((row) => row.join(",")).join("\n");
};
